import {
  createElement,
  useMemo,
  useRef,
  KeyboardEvent,
  useEffect,
  useState,
  useCallback,
  useImperativeHandle,
} from 'react';
import { DropdownDesignProps } from './';
import { useDesignTokens, renderInlineCssVars, DesignTokens } from '../../../theme';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { OptionPopper, OptionPopperProps, VirtualOptionPopperProps } from '../../OptionPopper';
import { DropdownOption } from '../../Dropdown/__internal__/types';
import { Virtualizer } from '../../ListBox';
import { isNullish } from '@sas/nova-core/typeguards';
import useOnFocusOut from '../../../hooks/useOnFocusOut';
import { useOnOutsideMouseDown } from '../../../hooks/useOnOutsideMouseDown';
import {
  findNextIndex,
  findPreviousIndex,
  findNextPageIndex,
  findPreviousPageIndex,
} from '../../../util/findIndex';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { SelectBoxDesign, SelectBoxStatus } from '../../Select/__internal__/';
import { renderSlotWithComponentFallback } from '../../../slot';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import bundle from '../NovaComponents-Dropdown-gui-icu.properties';
import { setRef } from '../../../util/ref-util';
import { getScrollbarSize } from '../../../util/dom/getScrollbarSize';
import { useMounted } from '../../../hooks/useMounted';
import { useDirectSpeech } from '../../DirectSpeech/DirectSpeechProvider';
import { useIsRTL } from '../../../locale';
import { convertCSSSizeToNumber } from '../../../util/dom';
import { getWidestText, measureTextWidth } from './utils/utils';
import { isOptionPopperSeparator } from './utils/typeguards';
import { Popover } from '../../Popover';
import { useEarcon, EarconName } from '../../../hooks/useEarcon';

import cx from 'classnames';
import cn from './DropdownDesign.css.json';
import './DropdownDesign.css';

const useListId = createUseHtmlId('Dropdown-list');
const useValidationMessageId = createUseHtmlId('Dropdown-ValidationMessage');

const useTextLocalizer = createUseTextLocalizer(bundle);

/**
 * @internal
 */
export const DropdownDesign = (props: DropdownDesignProps) => {
  const localize = useTextLocalizer();
  const isRTL = useIsRTL();
  const mounted = useMounted();
  const defaultPlaceholder = localize('placeholder.msg');
  const focusable = props.focusable ?? true;
  const isAutoWidth = !props.virtualItems ? !props.style?.width : false;
  // if auto width, then hide until final width
  const [visible, setVisible] = useState(!isAutoWidth);
  const [hasHover, setHasHover] = useState<boolean>(false);
  const [initialVisit, setInitialVisit] = useState(props.required);
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const hasUnselectedValue = !isNullish(props.unselectedValue);
  // null = no null item
  // undefined = default null item
  // object = custom null item
  const hasNullItem = hasUnselectedValue && props.unselectedValue?.item !== null;
  const placeholder = props.unselectedValue?.placeholder;
  const _isItemDisabled = props.isItemDisabled;
  const isItemDisabled = useCallback(
    (index: number) => {
      if (props.hasInput) return false; // isItemDisabled not supported in combobox
      if (hasNullItem && index === 0) return false;
      return !!_isItemDisabled?.(hasNullItem ? index - 1 : index);
    },
    [props.hasInput, hasNullItem, _isItemDisabled]
  );
  const onSelected = (index: number) => {
    if (index !== selectedIndex && !isItemDisabled?.(index)) {
      props.onSelected?.(!hasNullItem ? index : index > 0 ? index - 1 : undefined);
      setInitialVisit(false);
    }
  };
  const refSelectBox = useRef<HTMLDivElement | null>(null);
  const refTextElement = useRef<HTMLElement | null>(null);
  const refContainer = useRef<HTMLDivElement | null>(null);
  const refPopover = useRef<HTMLDivElement | null>(null);
  const refFocusedListItem = useRef<HTMLLIElement | null>(null);
  const validationMessageId = useValidationMessageId();
  const listId = useListId();
  const itemPreId = `${listId}-item`;
  const nullItemId = `${listId}-nullItem`;
  const nullItem = useMemo(() => {
    if (!hasNullItem) {
      return null;
    }
    const text = props.unselectedValue?.item?.text ?? placeholder ?? defaultPlaceholder;
    return { ...props.unselectedValue?.item, text, id: nullItemId };
  }, [props.unselectedValue, hasNullItem, placeholder, defaultPlaceholder, nullItemId]);

  const { items, displayListItemIcon } = useMemo<{
    items?: Array<DropdownOption>;
    displayListItemIcon?: boolean;
  }>(() => {
    // honor existing props.displayListItemIcon, if any.
    let displayListItemIcon = props.displayListItemIcon;

    if (!props.values || props.virtualItems) {
      return { items: undefined, displayListItemIcon };
    }

    const items: DropdownOption[] = [];
    let index = 0;

    if (nullItem) items.push(nullItem);

    let showSeparator = false;
    for (const value of props.values) {
      // no longer using a dedicated item separator, instead will
      // use showSeparator on the next item
      if (isOptionPopperSeparator(value)) {
        showSeparator = true;
        continue;
      } else {
        const item = value as DropdownOption;
        items.push({
          ...value,
          id: itemPreId + index++,
          showSeparator: item.showSeparator ?? showSeparator,
        });
        showSeparator = false;

        // find any item that has the icon
        if (displayListItemIcon === undefined && (value as any).icon !== undefined) {
          displayListItemIcon = true;
        }
      }
    }

    return { items, displayListItemIcon: !!displayListItemIcon };
  }, [props.values, props.virtualItems, props.displayListItemIcon, nullItem, itemPreId]);
  // only return values, excluding null item
  const getValue = useCallback(
    (index: number) =>
      props.virtualItems?.renderItem({ index }) ?? items?.[hasNullItem ? index + 1 : index],
    [hasNullItem, items, props.virtualItems]
  );
  // any items, including null item
  const getItem = useCallback(
    (index: number) => {
      if (hasNullItem) return index === 0 ? nullItem : getValue(index - 1);
      return getValue(index);
    },
    [getValue, hasNullItem, nullItem]
  );
  const length = Math.min(
    props.virtualItems ? props.virtualItems?.count + (hasNullItem ? 1 : 0) : items?.length ?? 0,
    295000
  );
  const defaultIndex = useMemo(
    () =>
      findNextIndex({
        length,
        start: 0,
        isValidIndex: (index: number) => !isItemDisabled?.(index),
      }) ?? -1,
    [length, isItemDisabled]
  );
  const getNormalizedIndex = (index?: number | null) => {
    if (isNullish(index)) {
      if (props.unselectedValue && !hasNullItem) return -1;
      return defaultIndex;
    }
    const len = props.virtualItems?.count ?? props.values?.length ?? 0;
    index = Math.max(-1, index);
    index = Math.min(index, len - 1);
    return hasNullItem && index !== -1 ? index + 1 : index ?? (props.hasInput ? -1 : 0);
  };
  const selectedIndex = getNormalizedIndex(props.selectedIndex);
  const focusedIndex = getNormalizedIndex(props.focusedIndex);
  const setFocusedIndex = (index: number) =>
    props.setFocusedIndex?.(!hasNullItem ? index : index > 0 ? index - 1 : undefined);
  const renderItem: VirtualOptionPopperProps['renderItem'] = useCallback(
    ({ index }) => {
      if (hasNullItem) {
        if (index <= 0) return nullItem;
        index--;
      }
      const value = getValue(index);
      return {
        ...value,
        id: itemPreId + index,
      };
    },
    [getValue, hasNullItem, itemPreId, nullItem]
  );
  const virtualizerRef = useRef<Virtualizer>();
  const pageSizeRef = useRef<number>();
  // make sure if width is not wide enough that popper's
  // width is not less than the combobox and should align
  const container = refContainer.current as HTMLDivElement;
  const containerWidth = container ? container.getBoundingClientRect().width : 0;
  const getListWidth = useCallback(() => {
    if (containerWidth > 0 && props.virtualItems?.width === undefined) {
      const totalPopperBorderWidth = 2;
      return containerWidth - totalPopperBorderWidth;
    }
    return props.virtualItems?.width;
  }, [containerWidth, props.virtualItems?.width]);
  const virtualItems: OptionPopperProps['virtualItems'] | undefined = useMemo(() => {
    return props.virtualItems
      ? {
          count: length,
          renderItem,
          virtualizerRef,
          pageSizeRef,
          width: getListWidth(),
          itemHeight: props.virtualItems.itemHeight,
        }
      : undefined;
  }, [props.virtualItems, length, renderItem, getListWidth]);

  const validIndex = !isNullish(focusedIndex) && focusedIndex < length && focusedIndex > -1;
  const focusedItemId = validIndex
    ? virtualItems
      ? renderItem({ index: focusedIndex })?.id
      : getItem(focusedIndex)?.id
    : undefined;

  const refScrollPosBuffer = useRef<number | undefined>(undefined);
  // used to scroll selected item at the top of the viewpoint when popover
  // is opened the first time
  const needsScrollingRef = useRef(false);

  const scrollPopover = useCallback(() => {
    if (virtualItems) {
      virtualizerRef?.current?.scrollToIndex?.(focusedIndex, {
        align: needsScrollingRef.current ? 'start' : 'auto',
      });
    } else {
      /**
       * NOTE: popper does not mount in the DOM until after the isOpen state change
       */
      // we don't need to scroll if the popper is not shown
      if (!refPopover.current || !refFocusedListItem.current) return;

      const offsetDelta = -(refPopover.current.offsetTop + refPopover.current.clientTop);
      const itemScrollTop = refFocusedListItem.current.offsetTop + offsetDelta;
      const itemScrollBottom = itemScrollTop + refFocusedListItem.current.offsetHeight;
      const scrollTop = refScrollPosBuffer.current ?? refPopover.current.scrollTop;

      if (itemScrollBottom > scrollTop + refPopover.current.clientHeight) {
        // Scroll down to the focused item
        refPopover.current.scrollTop = itemScrollBottom - refPopover.current.clientHeight;
      } else if (itemScrollTop < scrollTop) {
        // Scroll up to the focused item
        refPopover.current.scrollTop = itemScrollTop;
      } else if (refScrollPosBuffer.current !== undefined) {
        // Scroll to the position in our ref buffer
        refPopover.current.scrollTop = refScrollPosBuffer.current;
      }

      refScrollPosBuffer.current = refPopover.current.scrollTop;
    }
  }, [focusedIndex, virtualItems]);

  //  Make sure dropdown scrolls with the focused item
  useEffect(() => {
    scrollPopover();
  }, [focusedIndex, scrollPopover]);

  // Keep track of whether we still need to do our initial scroll each time popover opens
  useEffect(() => {
    needsScrollingRef.current = props.isOpen;
  }, [props.isOpen]);

  const closePopover = (shouldCommitSelection: boolean) => {
    if (shouldCommitSelection) {
      if (isItemDisabled?.(focusedIndex)) return;
      onSelected(focusedIndex);
    } else {
      setFocusedIndex(selectedIndex);
    }
    props.setIsOpen?.(false);
  };

  const expand = () => {
    props.setIsOpen?.(true);
    // moves the focus to the list
    refSelectBox?.current?.focus();
  };

  const toggleOpen = () => {
    if (props.disabled || props.editable === false) return;
    if (props.isOpen) {
      closePopover(true);
    } else if (props.onBeforeOpen) {
      props.onBeforeOpen().then((preventExpand) => {
        if (!preventExpand) {
          expand();
        }
      });
    } else {
      expand();
    }
  };

  // When there is a focus trap, like inside a dialog,
  // that focus is not lost but a drag on the outside
  // may still need to close the popper.
  useOnOutsideMouseDown([refPopover, refSelectBox], () => {
    // since trapping a focus seems delayed so using
    // timeout to wait for a focus, if any
    setTimeout(() => {
      if (document.activeElement === refSelectBox.current) {
        closePopover(true);
        setInitialVisit(false);
      }
    }, 50);
  });

  const getItemsPerPage = () => {
    // Default items per page is set to 10 according to the spec
    const defaultPageSize = 10;
    if (!props.isOpen) return defaultPageSize;
    if (virtualItems) return pageSizeRef.current ?? defaultPageSize;

    const scrollHeight = refPopover.current?.scrollHeight ?? 0;
    const pageHeight = refPopover.current?.offsetHeight ?? 0;
    const itemHeight = scrollHeight / length;
    return Math.round(
      itemHeight !== 0 && pageHeight !== 0 ? pageHeight / itemHeight : defaultPageSize
    );
  };

  const earcon = useEarcon({ source: EarconName.InputFull });
  const earconError = useEarcon({ source: EarconName.Error });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (length <= 0 || props.disabled || props.readOnly) {
      return;
    }

    let eventHandled = true;

    if (props.hasInput) {
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      setEditing(e.key !== 'Tab');
    }
    switch (e.key) {
      case 'ArrowUp':
        if (e.altKey) {
          toggleOpen();
        } else {
          const index = findPreviousIndex({
            length,
            start: Math.max(focusedIndex - 1, 0),
            isValidIndex: (index: number) => props.isOpen || !isItemDisabled?.(index),
          });
          if (props.isOpen) setFocusedIndex(index ?? 0);
          else if (index !== undefined) onSelected(index);
          if (index === 0) earcon.play();
        }
        break;
      case 'ArrowDown':
        if (e.altKey) {
          toggleOpen();
        } else {
          const index = findNextIndex({
            length,
            start: Math.min(focusedIndex + 1, length - 1),
            isValidIndex: (index: number) => props.isOpen || !isItemDisabled?.(index),
          });

          if (props.isOpen) setFocusedIndex(index ?? length - 1);
          else if (index !== undefined) onSelected(index);
          if (index === length - 1) earcon.play();
        }
        break;
      case 'ArrowLeft': /* falls-through */
      case 'ArrowRight':
        if (!props.isOpen || props.hasInput) {
          eventHandled = false;
        }
        break;
      case 'PageUp':
        {
          const index = findPreviousPageIndex({
            length,
            start: Math.max(focusedIndex - 1, 0),
            isValidIndex: (index: number) => props.isOpen || !isItemDisabled?.(index),
            pageSize: getItemsPerPage(),
          });
          if (props.isOpen) setFocusedIndex(index ?? 0);
          else if (index !== undefined) onSelected(index);
          if (index === 0) earcon.play();
        }
        break;
      case 'PageDown':
        {
          const index = findNextPageIndex({
            length,
            start: Math.min(focusedIndex + 1, length - 1),
            isValidIndex: (index: number) => props.isOpen || !isItemDisabled?.(index),
            pageSize: getItemsPerPage(),
          });
          if (index !== undefined) {
            if (props.isOpen) setFocusedIndex(index);
            else onSelected(index);
          }
          if (index === length - 1) earcon.play();
        }
        break;
      case 'Home':
        {
          const index = findNextIndex({
            length,
            start: 0,
            isValidIndex: (index: number) => props.isOpen || !isItemDisabled?.(index),
          });
          if (props.isOpen) setFocusedIndex(index ?? 0);
          else if (index !== undefined) onSelected(index);
        }
        break;
      case 'End':
        {
          const index = findPreviousIndex({
            length,
            start: length - 1,
            isValidIndex: (index: number) => props.isOpen || !isItemDisabled?.(index),
          });
          if (index !== undefined) {
            if (props.isOpen) setFocusedIndex(index);
            else onSelected(index);
          }
        }
        break;
      case ' ':
        toggleOpen();
        break;
      case 'Enter':
        if (props.isOpen) {
          closePopover(true);
        } else {
          eventHandled = false;
        }
        break;
      case 'Escape':
        if (props.isOpen) {
          e.preventDefault();
          e.stopPropagation();
          closePopover(false);
        } else {
          eventHandled = false;
        }
        break;
      default:
        if (length <= 0 || e.key.length !== 1 || props.hasInput) {
          eventHandled = false;
          break;
        }

        const key = e.key.toLowerCase();
        const index = findNextIndex({
          length,
          start: (focusedIndex + 1) % length,
          isValidIndex: (index: number) => {
            const value = getItem(index);
            return (
              !isItemDisabled?.(index) &&
              !!getItemText(value ?? undefined)
                ?.toLowerCase()
                .startsWith(key)
            );
          },
          wrap: true,
        });
        if (index !== undefined) props.isOpen ? setFocusedIndex(index) : onSelected(index);
        else earconError.play();
        break;
    }

    if (eventHandled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const getItemText = useCallback(
    (item: DropdownOption | undefined) => {
      if (isNullish(item)) {
        // possibility of incorrect focused index by the consumer?
        if (focusedIndex === -1) return props.hasInput ? undefined : '';
        return '';
      }

      // consumer may set selectedIndex to clear contents of combobox
      if (selectedIndex === -1 && props.hasInput) return undefined;

      let label = item.text;
      {
        // HACK: this is specific to VA-SDK dropdowns <https://gitlab.sas.com/dvr/html/va-sdk/issues/157>
        const secondaryText = item.secondaryText;
        if (props.displaySecondaryText && !isNullish(secondaryText)) {
          label += ` (${secondaryText})`;
        }
      }
      return label;
    },
    [focusedIndex, props.displaySecondaryText, props.hasInput, selectedIndex]
  );

  const showPlaceholder =
    hasUnselectedValue && ((hasNullItem && focusedIndex === 0) || focusedIndex === -1);
  const placeholderLabel = hasUnselectedValue ? placeholder ?? defaultPlaceholder : undefined;
  const value =
    length !== 0 && focusedIndex >= 0 && focusedIndex < length ? getItem(focusedIndex) : undefined;
  const itemDisabled = isItemDisabled?.(focusedIndex);
  const label =
    showPlaceholder || length === 0
      ? placeholderLabel
      : itemDisabled
      ? '\xa0'
      : getItemText(value ?? undefined);
  const icon = !itemDisabled && displayListItemIcon && value ? value.icon : undefined;
  const iconAriaLabel =
    !itemDisabled && displayListItemIcon && value ? value.iconAriaLabel : undefined;
  const iconTooltip = !itemDisabled && displayListItemIcon && value ? value.iconTooltip : undefined;

  const autoWidth = useCallback(() => {
    if (!virtualItems && isAutoWidth && refTextElement.current?.parentElement && length > 0) {
      const defaultMinWidth = 96;
      const style = window.getComputedStyle(refTextElement.current as Element);
      const widestText = getWidestText(refTextElement.current, Math.min(1000, length), (index) => {
        const value: DropdownOption | undefined = getValue(index);
        return {
          primary: value?.text ?? '',
          secondary: `(${value?.secondaryText})` ?? '',
          includeIcon: displayListItemIcon && !isNullish(value?.icon),
        };
      });
      const textWidth = props.displaySecondaryText ? widestText.withSecondary : widestText.primary;
      let width = textWidth ?? 0;

      if (placeholderLabel)
        width = Math.max(width, measureTextWidth(refTextElement.current, placeholderLabel));

      if (width) {
        const scrollbarSize = getScrollbarSize();
        // rtl in FF (NOVACOMMONS-4917) has text-index to handle cut off italic so
        // may need a little wider
        width = width + convertCSSSizeToNumber(style.textIndent);

        // account for the scrollbar to make sure focus outline can be visible
        width = width + scrollbarSize;

        refTextElement.current.parentElement.style.width = `${Math.ceil(width)}px`;

        if (!!!props.style?.minWidth) {
          const dropDownContainer = refContainer.current as HTMLDivElement;
          const dropdownRect = dropDownContainer.getBoundingClientRect();
          const textRect = refTextElement.current.getBoundingClientRect();

          const gap = !isRTL
            ? (textRect.left ?? 0) - (dropdownRect.left ?? 0)
            : (dropdownRect.right ?? 0) - (textRect.right ?? 0);

          // empty '' to allow default min-width from style class
          dropDownContainer.style.minWidth = width + gap < defaultMinWidth ? 'initial' : '';
        }
      }
    }

    setVisible(true);
  }, [
    virtualItems,
    isAutoWidth,
    length,
    props.style?.minWidth,
    props.displaySecondaryText,
    displayListItemIcon,
    placeholderLabel,
    getValue,
    isRTL,
  ]);

  const [setParentRef] = useResizeObserverRef<Element>(() => autoWidth());

  useEffect(() => {
    if (isAutoWidth) {
      const maxWidth = window.getComputedStyle(refContainer.current as Element).maxWidth;
      if (maxWidth.indexOf('%') > -1 && refContainer.current?.parentElement)
        setParentRef(refContainer.current?.parentElement);
    }

    return () => {
      setParentRef(null);
    };
  }, [isAutoWidth, setParentRef]);

  useEffect(autoWidth, [autoWidth]);

  const getStatus = () => {
    if (!props.required || !isNullish(props.status)) return props.status;
    if (props.disabled || props.readOnly) return 'none';
    const enforceRequired =
      (!initialVisit &&
        ((hasNullItem && focusedIndex === 0) || (!hasNullItem && focusedIndex === -1))) ||
      (props.hasInput &&
        focusedIndex === -1 &&
        props.value === '' &&
        (!hasFocus || (hasFocus && !editing)));
    return enforceRequired ? 'alert' : 'none';
  };
  const status = getStatus();

  useImperativeHandle(props.selectAPIRef, () => ({
    resetRequiredTrigger: () => setInitialVisit(props.required),
  }));

  const message =
    isNullish(props.message) && props.required && status === 'alert'
      ? localize('required.msg')
      : props.message;

  const handleMouseOver = () => {
    setHasHover(true);
  };

  const directSpeech = useDirectSpeech();
  let onBlur, onFocus;
  {
    const handlers = useOnFocusOut(() => {
      if (!mounted.current) return;
      closePopover(!isItemDisabled?.(focusedIndex));
      setHasFocus(false);
      setInitialVisit(false);
    });
    onBlur = () => {
      if (status && message) directSpeech?.notify(message);
      handlers.onBlur();
      if (props.hasInput) {
        setEditing(false);
      }
    };
    onFocus = () => {
      handlers.onFocus();
      setHasFocus(true);
      if (props.hasInput) {
        setEditing(false);
      }
    };
  }

  const handleMouseLeave = () => setHasHover(false);

  const dt = useDesignTokens();
  const dropdownMaxWidth = isAutoWidth ? props.style?.maxWidth ?? '25rem' : undefined;
  const iconWidth = 14;
  const style = {
    ...renderInlineCssVars(
      {
        'max-width': dropdownMaxWidth,
        'icon-width': `${iconWidth}px`,
        'font-size-m': dt.fontNova.size.m,
        visibility: visible ? 'visible' : 'hidden',
        ...getStatusTokens(dt, status),
      },
      'dropdown'
    ),
    ...renderInlineCssVars({ 'squish-s': dt.spacing.insetSquish.s.padding }, 'dropdown-spacing'),
  };
  const ariaActiveDescendant =
    !isNullish(focusedItemId) && props.isOpen ? focusedItemId : undefined;

  const showStatus = (status === 'warning' || status === 'alert') && !!message;

  const getAriaDescribedBy = () => {
    const ariaDescribedBy: string[] = [];

    if (showStatus) {
      ariaDescribedBy.push(validationMessageId);
    }

    if (Array.isArray(props.ariaDescribedBy)) {
      props.ariaDescribedBy.length && ariaDescribedBy.push(...props.ariaDescribedBy);
    } else if (!isNullish(props.ariaDescribedBy)) {
      ariaDescribedBy.push(props.ariaDescribedBy);
    }

    return ariaDescribedBy;
  };

  const { setIsOpen } = props;
  const onClose = useCallback(() => setIsOpen?.(false), [setIsOpen]);

  return (
    <div
      className={cx(cn.container, props.className)}
      style={{ ...style, ...props.style }}
      ref={refContainer}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      onBlur={onBlur}
      onFocus={onFocus}
      onContextMenu={(e) => e.preventDefault()}
    >
      {renderSlotWithComponentFallback(props.renderSelectBox, SelectBoxDesign, {
        innerRef: (selectBox) => {
          refSelectBox.current = selectBox;
          setRef(props.selectBoxRef, selectBox);
        },
        textRef: refTextElement,
        testId: props.testId,
        listId,
        isPlaceholder: showPlaceholder,
        label,
        onPress: toggleOpen,
        isOpen: props.isOpen,
        disabled: props.disabled,
        hasFocus,
        status,
        required: props.required,
        focusable,
        ariaActiveDescendant,
        ariaLabel: props.ariaLabel,
        ariaLabelledBy: props.ariaLabelledBy,
        ariaDescribedBy: getAriaDescribedBy(),
        icon,
        iconAriaLabel,
        iconTooltip,
        ...props.selectTokens,
        selectedIndex,
        focusedIndex,
        setFocusedIndex,
        tooltip: props.tooltip ?? getItem(focusedIndex)?.tooltip ?? '',
        readOnly: props.readOnly,
      })}
      <OptionPopper
        listId={listId}
        isOpen={props.isOpen}
        focusedIndex={focusedIndex}
        focusedItemRef={refFocusedListItem}
        showSelectedPseudoFocus={props.showSelectedPseudoFocus}
        referenceElementRef={refSelectBox}
        // Option popper should recieve the item list with the separators still included
        items={items}
        virtualItems={virtualItems}
        ref={(popover) => {
          // Need to know when our ref is defined so we can trigger scrolling immediately
          refPopover.current = popover;
          if (popover && needsScrollingRef.current) {
            scrollPopover();
            // This ref callback will be called sometimes while OptionPopper is open, only perform initial scroll once
            needsScrollingRef.current = false;
          }
        }}
        testId={props.testId}
        popoverTokens={props.popoverTokens}
        style={{
          maxWidth: '25rem',
          ...props.popoverStyles,
        }}
        listTokens={props.listTokens}
        listItemTokens={props.listItemTokens}
        ariaLabelledBy={props.ariaLabelledBy}
        ariaLabel={props.ariaLabel}
        onSelected={({ index }) => {
          if (isItemDisabled?.(index)) return;

          onSelected(index);
          props.setIsOpen?.(false);
          refSelectBox.current?.focus();
          // When selecting an item, store the current scrollTop in the buffer
          if (!virtualItems) refScrollPosBuffer.current = refPopover.current?.scrollTop || 0;
        }}
        displayListItemIcon={displayListItemIcon}
        onClose={onClose}
        focusLockProps={{ disabled: true }}
        isItemDisabled={isItemDisabled}
        disableContextMenu
      />
      {showStatus ? (
        <span aria-live="polite" role="status" className={cn.offscreen} id={validationMessageId}>
          {message}
        </span>
      ) : null}
      {props.isOpen || (!hasFocus && !hasHover) || !showStatus ? null : (
        <Popover
          hideArrow
          referenceElementRef={refContainer}
          className={cn.message}
          style={style}
          placement="bottom-start"
          focusLockProps={{ disabled: true }}
        >
          <span>{message}</span>
        </Popover>
      )}
    </div>
  );
};

const getStatusTokens = (dt: DesignTokens, status: SelectBoxStatus) => {
  const messageBorder = {
    'message-bd': dt.themeVariant === 'high-contrast' ? `1px solid ${dt.c.colorUiBorder}` : 'none',
  };
  switch (status) {
    case 'alert':
      return {
        ...messageBorder,
        'message-bg': dt.stateStatusPadBoxNova.alert.bg,
        'message-fg': dt.stateStatusPadBoxNova.alert.fg,
      };
    case 'warning':
      return {
        ...messageBorder,
        'message-bg': dt.stateStatusPadBoxNova.warning.bg,
        'message-fg': dt.stateStatusPadBoxNova.warning.fg,
      };
    default:
      return {};
  }
};
